/**
 * Contractor-facing meter suffix labels (TierWidget). Mirrors the server rule
 * in @tenfour/domain `hasBillableOverageRate`: only a **positive** overage
 * rate keeps the service running and accrues billable overage; null or ≤0
 * means exhaustion falls back to manual mode (never free-ride, never
 * overcharge). Keep this pure so the copy can be asserted without mounting React.
 */

export function hasBillableOverageRate(rate: number | null | undefined): boolean {
  return rate != null && rate > 0
}

/**
 * "used of included" value for the voice meter header. Small allowances read
 * in MINUTES ("12 of 60 min") — an hours rendering of a sub-hour allowance is
 * meaningless ("0.0 of 0.0 hrs"); two hours and up read in hours.
 */
export function voiceMeterValue(usedMinutes: number, includedMinutes: number): string {
  if (includedMinutes < 120) {
    return `${Math.round(usedMinutes)} of ${Math.round(includedMinutes)} min`
  }
  return `${(usedMinutes / 60).toFixed(1)} of ${(includedMinutes / 60).toFixed(1)} hrs`
}

/** "used of included" value for the SMS meter header. */
export function smsMeterValue(usedMessages: number, includedMessages: number): string {
  return `${usedMessages.toLocaleString()} of ${includedMessages.toLocaleString()}`
}

/**
 * "used of included" value for the AI usage meter header — "$0.42 of $10",
 * matching the Voice/Texts rows instead of a bare percentage (#305). Spend
 * always shows cents (small figures like $0.004 must not read as $0); the cap
 * drops cents when it's a whole dollar amount.
 */
export function aiMeterValue(monthUsd: number, capUsd: number): string {
  const cap = Number.isInteger(capUsd) ? `$${capUsd.toLocaleString()}` : `$${capUsd.toFixed(2)}`
  return `$${monthUsd.toFixed(2)} of ${cap}`
}

/** Suffix after "X of Y voice hrs" — empty while under the allowance. */
export function voiceMeterExhaustedSuffix(
  exhausted: boolean,
  overageRatePerHourUsd: number | null | undefined,
): string {
  if (!exhausted) return ''
  if (hasBillableOverageRate(overageRatePerHourUsd)) {
    return ` — overage $${overageRatePerHourUsd}/hr`
  }
  // null / 0 / negative → manual mode, not free overage
  return ' — limit reached'
}

/** Suffix after "X of Y texts" — empty while under the allowance. */
export function smsMeterExhaustedSuffix(
  exhausted: boolean,
  overageRatePer100Usd: number | null | undefined,
): string {
  if (!exhausted) return ''
  if (hasBillableOverageRate(overageRatePer100Usd)) {
    return ` — overage $${overageRatePer100Usd}/100`
  }
  return ' — limit reached'
}
